import { Pencil } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { OrderModel } from '@/types/model/Order'
import { TextInput } from '@/components/atoms'

interface OrderCustomerInfoProps {
    order: OrderModel.Order
    actions: {
        updateCustomerInfo: (info: any) => void
        updateAddress: (addressId: string) => void
    }
    isEditable?: boolean
}

interface CustomerFormValues {
    name: string
    phone: string
    email: string
}

interface DeliveryFormValues {
    address_id: string
}

export const OrderCustomerInfo = ({ order, actions, isEditable = true }: OrderCustomerInfoProps) => {
    const [isOpenCustomer, setIsOpenCustomer] = useState(false)
    const [isOpenDelivery, setIsOpenDelivery] = useState(false)

    const customerForm = useForm<CustomerFormValues>({
        defaultValues: {
            name: order.customer_info?.name || '',
            phone: order.customer_info?.phone || '',
            email: order.customer_info?.email || '',
        },
    })

    const deliveryForm = useForm<DeliveryFormValues>({
        defaultValues: {
            address_id: '',
        },
    })

    useEffect(() => {
        if (order) {
            customerForm.reset({
                name: order.customer_info?.name || '',
                phone: order.customer_info?.phone || '',
                email: order.customer_info?.email || '',
            })
            deliveryForm.reset({
                address_id: '',
            })
        }
    }, [order, customerForm, deliveryForm])

    const onCustomerSubmit = (data: CustomerFormValues) => {
        const payload = { ...data }
        if (!payload.email?.trim()) {
            delete (payload as Partial<CustomerFormValues>).email
        }
        actions.updateCustomerInfo(payload)
        setIsOpenCustomer(false)
    }

    const onDeliverySubmit = (data: DeliveryFormValues) => {
        if (data.address_id) {
            actions.updateAddress(data.address_id)
        }
        setIsOpenDelivery(false)
    }

    return (
        <Card>
            <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
                <CardTitle>Customer</CardTitle>
                {isEditable && (
                    <Popover open={isOpenCustomer} onOpenChange={setIsOpenCustomer}>
                        <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Edit customer">
                                <Pencil />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-80 p-4">
                            <div className="mb-3 space-y-0.5">
                                <h4 className="text-sm font-semibold text-ink">Edit customer</h4>
                                <p className="text-sm text-text-2">Update the name and contact details.</p>
                            </div>
                            <div className="space-y-3">
                                <TextInput
                                    control={customerForm.control}
                                    name="name"
                                    label="Name"
                                    placeholder="Customer name"
                                    errors={customerForm.formState.errors}
                                />
                                <TextInput
                                    control={customerForm.control}
                                    name="phone"
                                    label="Phone"
                                    placeholder="Phone number"
                                    errors={customerForm.formState.errors}
                                />
                                <TextInput
                                    control={customerForm.control}
                                    name="email"
                                    label="Email"
                                    placeholder="Email address"
                                    errors={customerForm.formState.errors}
                                />
                                <Button onClick={customerForm.handleSubmit(onCustomerSubmit)} className="w-full">
                                    Save
                                </Button>
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
                <p className="font-medium text-ink">{order.customer_info?.name || 'Guest checkout'}</p>
                <p className="tabular-nums text-text-2">{order.customer_info?.phone || 'No phone number'}</p>
                <p className="break-all text-text-2">{order.customer_info?.email || 'No email provided'}</p>
            </CardContent>

            {order.instruction && (
                <div className="space-y-1 border-t border-border-subtle p-4">
                    <p className="text-sm font-semibold text-ink">Customer instructions</p>
                    <p className="text-sm text-text-2">{order.instruction}</p>
                </div>
            )}
        </Card>
    )
}
